/** Superadmin-only PO-Workspace "Produktzuordnung": löst offene Shopify-Produktidentitäten auf.
 *  Mapping-only (keine Order/Finance/Inventory/Shopify-Mutation). Shopify bleibt Sales Truth —
 *  das Mapping beantwortet NUR "welcher interne Artikel", NICHT "was wurde bezahlt". */
import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Search, PackageCheck, Tag, Clock, AlertTriangle, RotateCcw, Store } from "lucide-react";

export default function ProductMappingTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [idx, setIdx] = useState(0);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searched, setSearched] = useState(false);
  const [quality, setQuality] = useState("good");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [decided, setDecided] = useState([]);
  const [showDecided, setShowDecided] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const [o, d] = await Promise.all([
        api.get("/admin/product-mapping/open"),
        api.get("/admin/product-mapping/decided"),
      ]);
      setData(o.data); setIdx(0); setDecided(d.data.items || []);
    } catch (e) { setErr(apiError(e)); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const groups = data?.groups || [];
  const cur = groups[idx];
  useEffect(() => { setQuality(cur?.suggested_quality || "good"); }, [idx, data]);  // eslint-disable-line react-hooks/exhaustive-deps

  const doSearch = async () => {
    if (q.trim().length < 2) { setResults([]); setSearched(false); return; }
    try { const r = await api.get(`/admin/product-mapping/search?q=${encodeURIComponent(q)}`); setResults(r.data.results || []); setSearched(true); }
    catch (e) { setErr(apiError(e)); }
  };

  const decide = async (payload) => {
    if (!cur) return;
    setBusy(true); setErr("");
    try {
      await api.post("/admin/product-mapping/decide", {
        shopify_variant_id: cur.shopify_variant_id, sku: cur.sku,
        name: cur.shopify_name, variant_title: cur.variant_title, ...payload,
      });
      setQ(""); setResults([]); setSearched(false);
      await load();  // Re-Load → nächster offener Fall (gelöste verschwinden)
    } catch (e) { setErr(apiError(e)); } finally { setBusy(false); }
  };

  const resetDecision = async (id) => {
    setBusy(true); setErr("");
    try { await api.post("/admin/product-mapping/reset", { id }); await load(); }
    catch (e) { setErr(apiError(e)); } finally { setBusy(false); }
  };

  if (loading) return <div className="p-6 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Lade offene Zuordnungen …</div>;

  return (
    <div className="max-w-3xl space-y-4" data-testid="product-mapping-tab">
      <div>
        <h3 className="font-heading font-medium">Produktzuordnung — historische Shopify-Positionen</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Beantwortet ausschließlich: <b>welcher interne Artikel</b> war das? Der Verkaufspreis bleibt
          Shopify Sales Truth. Mapping-only — keine Änderung an Orders, Finance, Lager oder Shopify.
        </p>
      </div>

      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 text-center" data-testid="mapping-progress">
        {[["Offene Identitäten", data?.open_identities, "text-chart-4"],
          ["Betroffene Orders", data?.affected_orders, ""],
          ["Messe (ausgeschl.)", data?.messe_excluded_orders, "text-primary"],
          ["Gemappt", data?.mapped, "text-primary"],
          ["Shopify-Only", data?.shopify_only, "text-primary"],
          ["Später", data?.later, ""]].map(([l, v, c]) => (
          <div key={l} className="rounded-md border border-border bg-card px-2 py-2">
            <div className={`font-heading text-lg ${c}`}>{v ?? 0}</div>
            <div className="text-xs text-muted-foreground">{l}</div>
          </div>
        ))}
      </div>

      {data?.messe_fairs?.length > 0 && (
        <div className="rounded-md border border-border bg-muted/30 px-3 py-2 text-xs" data-testid="mapping-messe-info">
          <div className="font-medium text-foreground mb-1">Messe-Zeitraum ausgeschlossen — während dieser Termine gab es keine Online-Bestellungen (bereits in der Messeabrechnung inkl. Lagerabgang erfasst):</div>
          <ul className="space-y-0.5">
            {data.messe_fairs.map((f) => (
              <li key={f.name} className="text-muted-foreground">
                <span className="text-foreground">{f.name}</span> ({f.start_date} – {f.end_date}): {f.orders_count} Order(s)
                {f.order_numbers?.length ? ` · ${f.order_numbers.slice(0, 8).join(", ")}${f.order_numbers.length > 8 ? " …" : ""}` : ""}
              </li>
            ))}
          </ul>
        </div>
      )}

      {err && <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive" data-testid="mapping-error">{err}</div>}

      {data?.evidence_available === false ? (
        <div className="rounded-md border border-chart-4/50 bg-chart-4/10 p-6 text-center" data-testid="mapping-no-evidence">
          <AlertTriangle className="h-6 w-6 text-chart-4 inline mb-2" />
          <div className="font-medium">Shopify-Evidenz aktuell nicht abrufbar.</div>
          <p className="text-xs text-muted-foreground mt-1">Die offenen Identitäten werden live aus Shopify geladen (dieselbe Quelle wie die Zero-Delta-Diagnose). Bitte Shopify-Verbindung/Netzwerk prüfen und erneut laden.</p>
        </div>
      ) : !cur ? (
        <div className="rounded-md border border-primary/40 bg-primary/5 p-6 text-center" data-testid="mapping-done">
          <PackageCheck className="h-6 w-6 text-primary inline mb-2" />
          <div className="font-medium">Keine offenen Produktidentitäten mehr. 🎉</div>
          <p className="text-xs text-muted-foreground mt-1">Führe die Root-Cause-Diagnose erneut aus — die gemappten Fälle gelten dann als AUTO_REBUILDABLE.</p>
        </div>
      ) : (
        <div className="rounded-md border border-border bg-card p-4 space-y-4" data-testid="mapping-current">
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">Fall {idx + 1} von {groups.length}</span>
            <span className="text-xs text-muted-foreground">{cur.orders_count} betroffene Order(s)</span>
          </div>
          <div>
            <div className="text-xs text-muted-foreground mb-1">Shopify Original (Sales Truth)</div>
            <div className="font-heading font-medium" data-testid="mapping-shopify-name">{cur.shopify_name || "—"}</div>
            <div className="mt-1 grid grid-cols-2 gap-x-6 gap-y-1 text-xs">
              <div>Variant Title: <span className="font-mono">{cur.variant_title || "—"}</span></div>
              <div>Variant ID: <span className="font-mono">{cur.shopify_variant_id || "—"}</span></div>
              <div>SKU: <span className="font-mono">{cur.sku || "—"}</span></div>
              <div>Menge gesamt: <span className="font-mono">{cur.quantity_total}</span></div>
              <div>Mapping-Typ: <span className="font-mono">{cur.mapping_type}</span></div>
              <div>Beispiel-Orders: <span className="font-mono">{(cur.order_numbers || []).slice(0, 6).join(", ")}</span></div>
            </div>
          </div>

          <div>
            <div className="text-xs text-muted-foreground mb-1.5">Qualität der Shopify-Position (bestimmt den internen Bestands-Bucket — <u>nicht</u> den Preis)</div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant={quality === "good" ? "default" : "outline"} onClick={() => setQuality("good")} data-testid="mapping-quality-good">1. Wahl</Button>
              <Button size="sm" variant={quality === "second_choice" ? "default" : "outline"} onClick={() => setQuality("second_choice")} data-testid="mapping-quality-second">2. Wahl</Button>
              {cur.suggested_quality === "second_choice" && (
                <span className="text-xs text-chart-4" data-testid="mapping-quality-detected">
                  2. Wahl erkannt (SKU-Suffix „_2" → Basis „{cur.base_sku}")
                </span>
              )}
            </div>
          </div>

          <div>
            <div className="text-xs text-muted-foreground mb-1">Interne Artikelsuche — <b>internen</b> Produktnamen/interne SKU eingeben (<u>nicht</u> die Shopify-SKU; kein Preis-Matching)</div>
            <div className="flex gap-2">
              <Input value={q} onChange={(e) => { setQ(e.target.value); setSearched(false); }} onKeyDown={(e) => e.key === "Enter" && doSearch()}
                placeholder="z. B. interner Produktname oder interne SKU …" data-testid="mapping-search-input" />
              <Button variant="outline" onClick={doSearch} data-testid="mapping-search-btn"><Search className="h-4 w-4" /></Button>
            </div>
            {results.length > 0 && (
              <div className="mt-2 max-h-56 overflow-auto rounded-md border border-border" data-testid="mapping-search-results">
                {results.map((r) => (
                  <button key={r.internal_entity_id} disabled={busy}
                    onClick={() => decide({ decision: "mapped", internal_entity_type: r.type, internal_entity_id: r.internal_entity_id, internal_variant_id: r.type === "variant" ? r.internal_entity_id : null, internal_label: `${r.name} (${r.sku || "—"})`, quality })}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-muted border-b border-border last:border-0 flex items-center justify-between gap-2"
                    data-testid="mapping-assign-result">
                    <span className="flex items-center gap-2">
                      {r.name}
                      <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase text-muted-foreground">
                        {r.type === "bundle" ? "Bundle" : r.type === "messe_bundle" ? "Messe-Bundle" : "Produkt"}
                      </span>
                    </span>
                    <span className="font-mono text-xs text-muted-foreground">{r.sku || "—"}{quality === "second_choice" ? " · 2. Wahl" : ""}</span>
                  </button>
                ))}
              </div>
            )}
            {searched && results.length === 0 && (
              <div className="mt-2 rounded-md border border-border bg-muted/40 px-3 py-2 text-sm text-muted-foreground" data-testid="mapping-no-results">
                Keine internen Treffer für „{q}". Die Shopify-SKU existiert nicht zwingend intern — suche nach dem <b>internen</b> Artikelnamen/der internen SKU, oder markiere die Position als <b>Shopify-Only</b>.
              </div>
            )}
          </div>

          <div className="flex flex-wrap gap-2 pt-1">
            <Button variant="outline" disabled={busy} onClick={() => decide({ decision: "shopify_only" })} data-testid="mapping-shopify-only-btn">
              <Tag className="h-4 w-4 mr-2" />Als Shopify-Only markieren
            </Button>
            <Button variant="outline" disabled={busy} onClick={() => decide({ decision: "messe" })} data-testid="mapping-messe-btn" title="Messe-Verkauf außerhalb der erkannten Messezeiträume: keine Online-Prüfung, kein Lagerabzug, keine Umsatzbuchung">
              <Store className="h-4 w-4 mr-2" />Als Messe markieren
            </Button>
            <Button variant="ghost" disabled={busy} onClick={() => setIdx((i) => Math.min(i + 1, groups.length - 1))} data-testid="mapping-later-btn">
              <Clock className="h-4 w-4 mr-2" />Später entscheiden
            </Button>
            {busy && <Loader2 className="h-4 w-4 animate-spin self-center" />}
          </div>
        </div>
      )}

      {decided.length > 0 && (
        <div className="rounded-md border border-border bg-card" data-testid="mapping-decided">
          <button onClick={() => setShowDecided((s) => !s)}
            className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium hover:bg-muted/50"
            data-testid="mapping-decided-toggle">
            <span>Bereits entschieden — erneut prüfbar ({decided.length})</span>
            <span className="text-xs text-muted-foreground">{showDecided ? "ausblenden" : "anzeigen"}</span>
          </button>
          {showDecided && (
            <div className="max-h-96 overflow-auto border-t border-border">
              {decided.map((d) => (
                <div key={d.id} className="flex items-center justify-between gap-2 px-3 py-2 text-sm border-b border-border last:border-0" data-testid={`mapping-decided-row-${d.id}`}>
                  <div className="min-w-0">
                    <div className="truncate">{d.shopify_name || "—"}
                      {d.variant_title && <span className="ml-1 text-xs text-muted-foreground">· {d.variant_title}</span>}
                    </div>
                    <div className="mt-0.5 flex items-center gap-2 text-xs">
                      {d.decision === "shopify_only" ? (
                        <span className="rounded bg-chart-4/15 px-1.5 py-0.5 font-medium uppercase text-chart-4">Shopify-Only</span>
                      ) : d.decision === "messe" ? (
                        <span className="rounded bg-primary/15 px-1.5 py-0.5 font-medium uppercase text-primary">Messe</span>
                      ) : (
                        <span className="rounded bg-primary/10 px-1.5 py-0.5 font-medium uppercase text-primary">Gemappt</span>
                      )}
                      {d.decision === "mapped" && <span className="text-muted-foreground truncate">→ {d.internal_label || "—"}{d.quality === "second_choice" ? " · 2. Wahl" : ""}</span>}
                      {d.sku && <span className="font-mono text-muted-foreground">{d.sku}</span>}
                    </div>
                  </div>
                  <Button size="sm" variant="outline" disabled={busy} onClick={() => resetDecision(d.id)} data-testid={`mapping-reset-${d.id}`}>
                    <RotateCcw className="h-3.5 w-3.5 mr-1.5" />Erneut prüfen
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
